// Third-party
import React, { PureComponent } from 'react';
import classNames from 'classnames';
import throttle from 'lodash/throttle';
import { connect } from 'react-redux';
import { Sticky, StickyContainer } from 'react-sticky';

// Actions
import { ReduxState } from '@/redux/modules/types';
import {
  clearMarkup,
  getItemHoldingsData,
  postMarkupComment,
  removeItemFromGroup,
  selectCategory,
  selectComparisonItem,
  setCalendarYearPeriod,
  setExpandedFields,
  setMarkupComment,
  setRiskPeriod,
  toggleCategory,
  toggleField,
  toggleItemFX,
  unselectAllComparisonItems,
  unselectComparisonItem,
  updateComparisonItemCategory,
  updateComparisonItemColor,
  updateComparisonItemHolding,
} from '@/redux/modules/comparison';
import { ComparisonItemsFilter } from '@/redux/modules/types/comparison';
import { getPortfolioById } from '@/redux/modules/portfolios';

// Components
import ComparisonCard from './ComparisonCard';
import ComparisonScrollbarContainer from '../misc/ComparisonScrollbarContainer';
import ComparisonTableLabels from './ComparisonTableLabels';
import ComparisonTableOptions from './ComparisonTableOptions';
import VisibilityChecker from '@/views/components/misc/VisibilityChecker';
import { ActionBar } from '@/views/components/misc';
import { CorrelationModal } from '@/views/components/modals';
import { LoadingSpinner } from '@/views/components/common';
import { withErrorBoundary } from '@/views/components/errors';

// Types
import User from '@/types/user';
import ComparisonItem from '@/types/comparison-item';
import ComparisonItemCategory from '@/types/comparison-item-category';
import BenchmarkPortfolio from '@/types/benchmark-portfolio';
import Portfolio from '@/types/portfolio';
import ComparisonCategory from '@/types/comparison-category';
import ComparisonField from '@/types/comparison-field';
import ComparisonHolding from '@/types/comparison-holding';
import Alt from '@/types/models/alt';
import Fund from '@/types/fund';
import Stock from '@/types/stock';
import Benchmark from '@/types/benchmark';
import Nullable from '@/types/nullable';
import Comparison from '@/types/comparison';
import ComparisonItemGroup from '@/types/comparison-item-group';
import Locale from '@/types/locale';

// Utils
import dom from '@/utils/dom';
import { calcNewDotNumber, itemHasComments } from '@/utils/markup-tools';
import { COMPARISON_CATEGORIES, COMPARISON_CELL_HEIGHT, COMPARISON_ITEM_TYPES } from '@/utils/constants';
import { comparisonCategoryIsShown, getDuplicateFields, isBenchmark } from '@/utils/comparison';
import { formatMessage } from '@/utils/i18n';
import { PERMISSIONS, userHasPermissionEnabled } from '@/utils/permissions';
import { TABLET_BREAKPOINT } from '@/hooks/useScreenSize';

type StateProps = {
  editedPortfolio: Portfolio;
  editedComparisonItem?: ComparisonItem;
  selectedComparisonItems: ComparisonItem[];
  isEditPinned: boolean;
  locale: Locale;
  editedBenchmark?: BenchmarkPortfolio;
};

type DispatchProps = {
  getPortfolioById: (
    id: string,
    portfolioType?: string,
    contact?: string,
    enterprise?: any,
    errorWhiteList?: string[],
    source?: Portfolio,
  ) => Promise<Portfolio>;
  selectComparisonItem: (item: ComparisonItem, unselectOthers?: boolean) => void;
  unselectComparisonItem: (item: ComparisonItem) => void;
  unselectAllComparisonItems: () => void;
  selectCategory: (category: ComparisonCategory, availableFields: ComparisonField[], unselectedFields?: ComparisonField[]) => void;
  toggleField: (category: ComparisonCategory, field: ComparisonField) => void;
  setExpandedFields: (fieldKey: string) => void;
  setMarkupComment: (item: ComparisonItem, category: ComparisonCategory, field: ComparisonField, comment: string) => void;
  postMarkupComment: (item: ComparisonItem, category: ComparisonCategory, field: ComparisonField, dotNumber: number, comment: string) => void;
  clearMarkup: (item: ComparisonItem, categoryKey: string, fieldKey: string, markupTool: string) => void;
  getItemHoldingsData: (holdings: ComparisonHolding[]) => Promise<{ alts: Alt[]; funds: Fund[]; stocks: Stock[]; benchmarks: Benchmark[] }>;
  setRiskPeriod: (period: Nullable<number>) => void;
  setCalendarYearPeriod: (period: Nullable<number>) => void;
  toggleItemFX: (item: ComparisonItem, toggled: boolean) => void;
  toggleCategory: (category: ComparisonCategory) => void;
  updateComparisonItemHolding: (item: ComparisonItem, holdingId: string, holdingData: Partial<ComparisonHolding>) => void;
  updateComparisonItemColor: (item: ComparisonItem, color: string) => void;
  updateComparisonItemCategory: (item: ComparisonItem, category: ComparisonItemCategory | undefined) => void;
  popoverContainerRef: Nullable<HTMLDivElement>;
  removeItemFromGroup: (itemId: string, groupId: string) => void;
};

export type Props = DispatchProps &
  StateProps & {
    user: User;
    readOnly?: boolean;
    comparison: Comparison;
    rawComparison: Comparison;
    itemsFilter: string[];
    items: ComparisonItem[];
    selection?: boolean;
    onItemRemove: (item: ComparisonItem) => void;
    onModifyPortfolio: (item: ComparisonItem) => void;
    onViewPortfolio: (item: ComparisonItem) => void;
    onModifyGroup: (item: ComparisonItem) => void;
    onSavePortfolio: (item: ComparisonItem, toContact?: boolean) => void;
    onClonePortfolio: (item: ComparisonItem) => void;
    onExpandPortfolioItem: (item: ComparisonItem, expand: boolean) => void;
    categories: ComparisonCategory[];
    addCategoryBenchmark: (item: ComparisonItem) => void;
    onCloneBenchmark: (item: ComparisonItem) => Promise<void>;
    onCombineIntoPortfolio: () => void;
    multiselect?: boolean;
    onCellClick: (item: ComparisonItem, category: ComparisonCategory, field: ComparisonField) => void;
    resetMarkup: () => void;
    setDuplicateFields: (duplicateFields: string[]) => void;
    duplicateFields?: string[];
    allowItemSelection?: boolean;
    markupMode?: boolean;
    markupTool?: string;
    updateSelectedCategories: (previousCategories: ComparisonCategory[], currentCategories: ComparisonCategory[], duplicateFields?: string[]) => void;
    onRiskChange: (value: number) => void;
    onCalendarYearChange: (value: number) => void;
    showActionBar: boolean;
    showGroups?: boolean;
    showAccountSets?: boolean;
    editGroups?: boolean;
    previewMarkup: boolean;
    togglePreviewMarkup: () => void;
    onModifyBenchmark: (item: ComparisonItem) => void;
    onSaveBenchmark: (benchmarkPortfolio: BenchmarkPortfolio) => Promise<void>;
    onEditBenchmarkCancel: (benchmarkPortfolio: BenchmarkPortfolio) => void;
    popoverContainerRef: Nullable<HTMLDivElement>;
    onAddItemToGroup: (item: ComparisonItem, group: ComparisonItemGroup, value: number, fee?: number) => void;
    onRemoveItemFromGroup: (item: ComparisonItem, group: ComparisonItemGroup) => void;
    onRemoveSelectedComparisonItems: () => void;
    onChangeComparisonItemsFilter: (filter: string[]) => void;
    onAddGroup: () => void;
    onManageAccountSets: () => void;
    tabSelections?: Record<string, number>;
    onTabSelectionChange?: (categoryKey: string, value: number) => void;
  };

type State = {
  viewMarkup: boolean;
  commentLinesWidth?: number;
  tableHeight?: number;
  showCorrelationModalItem: Nullable<ComparisonItem>;
  scrollContainerRef?: HTMLElement;
  hasStickyHeader?: boolean;
  shouldLoadAllCards?: boolean;
  disableStickyOption?: boolean;
  tableBarsWidth?: number;
};

class ComparisonTable extends PureComponent<Props, State> {
  private comparisonTableRef = React.createRef<HTMLDivElement>();

  private comparisonScrollbarContainerRef: ComparisonScrollbarContainer | null = null;

  static defaultProps = {
    multiselect: true,
    selection: true,
    showActionBar: true,
  };

  readonly state: State = {
    viewMarkup: true,
    showCorrelationModalItem: null,
    hasStickyHeader: false,
    shouldLoadAllCards: false,
    disableStickyOption: !!(window.innerWidth <= TABLET_BREAKPOINT),
  };

  handleResize = throttle(() => {
    const { editedBenchmark, editedComparisonItem, editedPortfolio, isEditPinned } = this.props;
    const hasActiveEditItem = !!editedComparisonItem || isEditPinned || !!editedPortfolio._id || !!editedBenchmark;
    const isTablet: boolean = !!(window.innerWidth <= TABLET_BREAKPOINT);
    return this.setState({
      disableStickyOption: hasActiveEditItem || isTablet,
      tableBarsWidth: (window.innerWidth - (this.comparisonTableRef.current?.offsetWidth || 0)) / 2,
    });
  }, 20);

  componentDidMount() {
    this.resizeCommentLinesContainer();
    this.initializeSelectedCategories();

    this.setState({ scrollContainerRef: this.getScrollContainerRef() });
    window.addEventListener('resize', this.handleResize);
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const {
      items,
      isEditPinned,
      categories,
      duplicateFields,
      markupMode,
      comparison: { selectedCategories },
      user,
      previewMarkup,
      readOnly,
      allowItemSelection,
    } = this.props;

    const { viewMarkup } = this.state;
    if (prevProps.items.length !== items.length || prevProps.isEditPinned !== isEditPinned) {
      setTimeout(() => {
        this.resizeCommentLinesContainer();
      }, 1000);
    }

    const allowItemSelectionChange = prevProps.allowItemSelection !== allowItemSelection;
    const readOnlyChange = prevProps.readOnly !== readOnly;
    const previewMarkupChange = prevProps.markupMode !== markupMode;

    if (allowItemSelectionChange) {
      if (allowItemSelection && items.length > 0) {
        this.props.unselectAllComparisonItems();
        this.props.selectComparisonItem(items[0]);
      }

      if (!allowItemSelection) {
        this.props.unselectAllComparisonItems();
      }
    }

    if ((!readOnly && previewMarkupChange) || (!readOnly && readOnlyChange)) {
      this.initializeSelectedCategories();
    }

    const prevTypes = new Set(prevProps.items.map(i => i.type));
    const currentTypes = new Set(items.map(i => i.type));

    const categoriesChanged =
      markupMode === prevProps.markupMode && prevProps.categories.length !== categories.length && prevProps.previewMarkup === previewMarkup;

    const hasMarkupPermission = userHasPermissionEnabled(user, PERMISSIONS.COMPARISON_MARKUP);

    // If theres new fields added to a category or the categories have changed (toggled) then we update the selected categories.
    if ((prevTypes.size !== currentTypes.size || categoriesChanged) && selectedCategories && hasMarkupPermission) {
      const fields = getDuplicateFields(categories);
      this.props.setDuplicateFields(fields);
      this.props.updateSelectedCategories(prevProps.categories, categories, duplicateFields);
    }

    if (prevState.viewMarkup !== viewMarkup || prevProps.previewMarkup !== previewMarkup || prevProps.categories !== categories) {
      this.resizeCommentLinesContainer();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  initializeSelectedCategories = () => {
    const {
      comparison: { selectedCategories },
      markupMode,
      categories,
    } = this.props;
    if (!markupMode) {
      return;
    }

    const fields = getDuplicateFields(categories);
    this.props.setDuplicateFields(fields);

    if (!selectedCategories) {
      this.props.resetMarkup();
    }
  };

  resizeCommentLinesContainer = () => {
    const { viewMarkup } = this.state;

    const commentSections = document.getElementsByClassName('comment-section');
    const lastCommentSection = commentSections[commentSections.length - 1] as HTMLDivElement;

    if (!viewMarkup || !lastCommentSection) {
      return;
    }

    this.setState({ commentLinesWidth: lastCommentSection.offsetLeft });
  };

  itemIsBeingEdited = (item: ComparisonItem) => {
    const { editedPortfolio } = this.props;

    return !!editedPortfolio._id && editedPortfolio._id === item._id;
  };

  benchmarkIsBeingEdited = (item: ComparisonItem) => {
    const { editedBenchmark } = this.props;

    return !!editedBenchmark && !!editedBenchmark._id && editedBenchmark._id === item._id;
  };

  itemIsSelected = (item: ComparisonItem) => this.props.selectedComparisonItems.some(i => i._id === item._id);

  onCombineClick = () => {
    this.props.onCombineIntoPortfolio();
  };

  onCopyClick = (item: ComparisonItem) => {
    if (item.type === COMPARISON_ITEM_TYPES.PORTFOLIO) {
      this.props.onClonePortfolio(item);
    }

    if (item.type === COMPARISON_ITEM_TYPES.BENCHMARK) {
      this.props.onCloneBenchmark(item);
    }
  };

  onModifyClick = (item: ComparisonItem) => {
    if (item.type === COMPARISON_ITEM_TYPES.GROUP) {
      this.props.onModifyGroup(item);
    }

    if (item.type === COMPARISON_ITEM_TYPES.PORTFOLIO) {
      this.props.onModifyPortfolio(item);
    }

    if (item.type === COMPARISON_ITEM_TYPES.BENCHMARK) {
      this.props.onModifyBenchmark(item);
    }

    if (item.type === COMPARISON_ITEM_TYPES.GROUP) {
      this.props.onModifyGroup(item);
    }
  };

  onExpandPortfolio = (item: ComparisonItem, expand: boolean) => {
    if (item.type !== COMPARISON_ITEM_TYPES.PORTFOLIO) {
      return;
    }

    this.props.onExpandPortfolioItem(item, expand);

    // If we're expanding a portfolio, toggle view markup off to ensure proper layout.
    if (expand) {
      this.toggleViewMarkup(false);
    }
  };

  onToggleFXItem = (item: ComparisonItem, toggled: boolean) => {
    this.props.toggleItemFX(item, toggled);

    setTimeout(() => {
      dom.fireResizeEvent();
    }, 50);
  };

  onRiskChange = (value: Nullable<number>) => {
    this.props.setRiskPeriod(value);
  };

  onCalendarYearChange = (value: Nullable<number>) => {
    this.props.setCalendarYearPeriod(value);
  };

  onSelectItem = (item: ComparisonItem) => {
    const { readOnly, selection, multiselect, allowItemSelection } = this.props;

    if (!selection) {
      return;
    }

    const isSelected = this.itemIsSelected(item);

    if (readOnly && !allowItemSelection) {
      return;
    }

    if (!isSelected) {
      if (!multiselect) {
        this.props.unselectAllComparisonItems();
      }

      if (allowItemSelection && this.props.selectedComparisonItems.length > 1) {
        this.props.unselectComparisonItem(this.props.selectedComparisonItems[0]);
        this.props.selectComparisonItem(item);
        return;
      }

      this.props.selectComparisonItem(item);
    } else if (!item._id.startsWith('new-')) {
      this.props.unselectComparisonItem(item);
    }

    // Fire a resize event to adjust the sticky headers - selecting a card messes up with the height on that card's header.
    setTimeout(() => {
      dom.fireResizeEvent();
    }, 100);
  };

  toggleViewMarkup = (enabled?: boolean) => {
    // If any of the items are expanded, collapse them before showing markup.
    const { items } = this.props.comparison;
    for (const item of items.filter(i => i.isExpanded)) {
      this.props.onExpandPortfolioItem(item, false);
    }

    this.setState(prevState => ({
      viewMarkup: enabled !== undefined ? enabled : !prevState.viewMarkup,
    }));
  };

  getNewDotNumber = () => {
    const { items } = this.props;
    return calcNewDotNumber(items);
  };

  updateTableHeight = (height: number) => {
    const cardsContainer = document.getElementsByClassName('comparison-cards')[0] as HTMLDivElement;

    if (height > cardsContainer.scrollHeight) {
      this.setState({ tableHeight: height });
    }
  };

  showCorrelationModal = (item: ComparisonItem) => {
    this.setState({ showCorrelationModalItem: item });
  };

  onCloseCorrelationModal = () => {
    this.setState({ showCorrelationModalItem: null });
  };

  displayExpandedComments = () => {
    const { user, markupMode } = this.props;

    const hasMarkupPermission = userHasPermissionEnabled(user, PERMISSIONS.COMPARISON_MARKUP);
    return (hasMarkupPermission && markupMode) || !hasMarkupPermission;
  };

  hideExpandedComments = () => {
    const { user, previewMarkup } = this.props;
    const { viewMarkup } = this.state;

    const hasMarkupPermission = userHasPermissionEnabled(user, PERMISSIONS.COMPARISON_MARKUP);
    if (hasMarkupPermission) {
      return !previewMarkup;
    }

    return !viewMarkup;
  };

  getAdditionalCategoryStyles = (category: ComparisonCategory) => {
    const { items } = this.props;
    let additionalStyles = {};
    const hasBenchmarks = items.some(i => isBenchmark(i));

    if (hasBenchmarks && category.key === COMPARISON_CATEGORIES.KEY_METRICS) {
      // Height includes category disclaimer
      const defaultHeight = 360;
      const calculatedHeight = (category.fields.length + 1) * COMPARISON_CELL_HEIGHT;

      additionalStyles = {
        height: `${Math.max(defaultHeight, calculatedHeight)}px`,
      };
    }

    return additionalStyles;
  };

  getCategories = () => {
    const { categories, comparison, markupMode } = this.props;
    return !markupMode ? categories : categories.filter(c => comparisonCategoryIsShown(comparison, c));
  };

  /**
   * Handles the visibility change of the component.
   * If the component becomes visible or the number of items is less than or equal to 6,
   * it sets the state to load all comparison item cards.
   *
   * @param {boolean} isVisible - A boolean indicating whether the component is visible.
   */
  onVisibilityChange = (isVisible: boolean) => {
    if (isVisible || this.props.items.length <= 6) {
      this.setState({ shouldLoadAllCards: true });
    }
  };

  renderCard = (min: number, max: number) => (item: ComparisonItem, i: number) => {
    const {
      editedComparisonItem,
      isEditPinned,
      onCellClick,
      markupMode,
      markupTool,
      selection,
      comparison,
      rawComparison,
      previewMarkup,
      readOnly,
      editedPortfolio,
      editedBenchmark,
      showGroups,
      editGroups,
      locale,
    } = this.props;

    if (i < min || i > max) {
      return null;
    }

    const { disableStickyOption, viewMarkup, scrollContainerRef, hasStickyHeader } = this.state;
    const { selectedCategories, riskPeriod } = comparison;

    const isEdited = this.itemIsBeingEdited(item);
    const isBenchmarkEdited = this.benchmarkIsBeingEdited(item);

    const classes = classNames({
      'editing-mode': (isEdited && isEditPinned) || isBenchmarkEdited,
      'markup-mode': markupMode,
      'no-selection': !selection,
      'read-only': readOnly,
    });

    return (
      <React.Fragment key={`${item._id}-order-${i}`}>
        <ComparisonCard
          className={classes}
          comparison={rawComparison}
          item={(isEdited && editedComparisonItem) || item}
          user={this.props.user}
          editedBenchmark={editedBenchmark}
          readOnly={this.props.readOnly}
          editBenchmarkMode={isBenchmarkEdited}
          pinnedEditMode={isEditPinned}
          categories={this.getCategories()}
          onSavePortfolio={this.props.onSavePortfolio}
          onModifyPortfolio={this.props.onModifyPortfolio}
          onViewPortfolio={this.props.onViewPortfolio}
          onClonePortfolio={this.props.onClonePortfolio}
          addCategoryBenchmark={this.props.addCategoryBenchmark}
          onCloneBenchmark={this.props.onCloneBenchmark}
          onItemRemove={this.props.onItemRemove}
          onSaveBenchmark={this.props.onSaveBenchmark}
          onEditBenchmarkCancel={this.props.onEditBenchmarkCancel}
          isSelected={this.itemIsSelected(item)}
          onSelectItem={this.onSelectItem}
          markupMode={markupMode}
          viewMarkup={viewMarkup}
          previewMarkup={previewMarkup}
          markupTool={markupTool}
          onCellClick={onCellClick}
          selectedCategories={selectedCategories}
          setMarkupComment={this.props.setMarkupComment}
          postMarkupComment={this.props.postMarkupComment}
          getNewDotNumber={this.getNewDotNumber}
          clearMarkup={this.props.clearMarkup}
          updateTableHeight={this.updateTableHeight}
          resizeCommentLinesContainer={this.resizeCommentLinesContainer}
          riskPeriod={riskPeriod}
          onShowCorrelation={this.showCorrelationModal}
          getItemHoldingsData={this.props.getItemHoldingsData}
          onExpandPortfolioItem={this.onExpandPortfolio}
          getAdditionalCategoryStyles={this.getAdditionalCategoryStyles}
          onToggleFXItem={this.onToggleFXItem}
          disableStickyHeader={disableStickyOption}
          onModifyBenchmark={this.props.onModifyBenchmark}
          editedPortfolio={editedPortfolio}
          updateComparisonItemHolding={this.props.updateComparisonItemHolding}
          updateComparisonItemColor={this.props.updateComparisonItemColor}
          updateComparisonItemCategory={this.props.updateComparisonItemCategory}
          popoverContainerRef={this.props.popoverContainerRef}
          scrollbarContainerRef={scrollContainerRef}
          hasStickyHeader={hasStickyHeader}
          onAddItemToGroup={this.props.onAddItemToGroup}
          onRemoveItemFromGroup={this.props.onRemoveItemFromGroup}
          onModifyGroup={this.props.onModifyGroup}
          showGroups={showGroups}
          editGroups={editGroups}
          locale={locale}
        />

        {this.displayExpandedComments() && itemHasComments(item, selectedCategories) && (
          // Comments are generated through a portal on the comparison card content component
          <div className={classNames('comment-section', { hidden: this.hideExpandedComments() })} id={`comment-section-${item._id}`} />
        )}
      </React.Fragment>
    );
  };

  toggleCategory = (category: ComparisonCategory) => {
    this.props.toggleCategory(category);
  };

  getScrollContainerRef = () => (this.comparisonScrollbarContainerRef ? this.comparisonScrollbarContainerRef.getScrollContainerRef() : undefined);

  getTablePaddingRef = () => {
    if (!this.comparisonScrollbarContainerRef) {
      return undefined;
    }

    return this.comparisonScrollbarContainerRef.getPaddingRef() || undefined;
  };

  onScrollX = (atStart?: boolean) => {
    if (!this.comparisonTableRef.current) {
      return;
    }

    if (atStart) {
      this.comparisonTableRef.current.classList.remove('is-scrolled-x');
    } else {
      this.comparisonTableRef.current.classList.add('is-scrolled-x');
    }
  };

  setStickyHeaderState = (isSticky: boolean = false) => {
    this.setState({ hasStickyHeader: isSticky });
  };

  onChangeComparisonFilter = (filter: string[]) => {
    this.props.unselectAllComparisonItems();

    const newFilter = filter.length === 0 ? [ComparisonItemsFilter.All] : filter;
    this.props.onChangeComparisonItemsFilter(newFilter);
  };

  onAddGroup = async () => {
    this.props.onAddGroup();
  };

  render() {
    const {
      user,
      isEditPinned,
      readOnly,
      comparison,
      rawComparison,
      itemsFilter,
      editedComparisonItem,
      items,
      selectedComparisonItems,
      markupMode,
      duplicateFields,
      showActionBar,
      previewMarkup,
      showGroups,
      showAccountSets,
      editGroups,
      onManageAccountSets,
    } = this.props;
    const { disableStickyOption } = this.state;

    const { calendarYearPeriod, riskPeriod } = comparison;

    if (!user) {
      return null;
    }

    const classes = classNames('comparison-table comparison-table-zebra-stripes d-flex', {
      'markup-mode': markupMode && !previewMarkup,
    });

    const { showCorrelationModalItem, viewMarkup, commentLinesWidth, tableHeight, scrollContainerRef, hasStickyHeader, tableBarsWidth } = this.state;

    const scrollbarContainerStyle =
      tableHeight && viewMarkup
        ? {
            height: tableHeight,
          }
        : undefined;

    const comparisonScrollbarClasses = classNames('comparison-cards', {
      'wide-padding': (markupMode && !previewMarkup) || (!markupMode && !viewMarkup),
    });

    const showTableStickyBars = hasStickyHeader && tableBarsWidth && tableBarsWidth >= 10;

    return (
      <>
        <ComparisonTableOptions
          readOnly={!editGroups}
          showGroups={showGroups}
          showAccountSets={showAccountSets}
          items={rawComparison.items || []}
          onAddGroup={this.onAddGroup}
          onManageAccountSets={onManageAccountSets}
          filter={itemsFilter}
          onChangeFilter={this.onChangeComparisonFilter}
          user={user}
        />

        <StickyContainer>
          <div className={classes} ref={this.comparisonTableRef} role="table" aria-label={formatMessage('Comparison.Table.AriaLabel')}>
            {!disableStickyOption && (
              <Sticky topOffset={-6}>
                {({ style, isSticky }) => {
                  if (isSticky !== hasStickyHeader) {
                    this.setStickyHeaderState(isSticky);
                  }
                  return <div style={style} className={classNames('sticky-padding', { 'is-sticky': isSticky })} />;
                }}
              </Sticky>
            )}

            <ComparisonTableLabels
              calendarYearPeriod={calendarYearPeriod}
              user={user}
              comparison={comparison}
              categories={this.getCategories()}
              selectCategory={this.props.selectCategory}
              toggleField={this.props.toggleField}
              setExpandedFields={this.props.setExpandedFields}
              selectedCategories={comparison.selectedCategories}
              markupMode={markupMode}
              duplicateFields={duplicateFields}
              viewMarkup={viewMarkup}
              toggleViewMarkup={this.toggleViewMarkup}
              previewMarkup={previewMarkup}
              togglePreviewMarkup={this.props.togglePreviewMarkup}
              items={items}
              onRiskChange={this.onRiskChange}
              onCalendarYearChange={this.onCalendarYearChange}
              riskPeriod={riskPeriod}
              scrollContainerRef={scrollContainerRef}
              tablePaddingRef={this.getTablePaddingRef()}
              editedComparisonItem={editedComparisonItem}
              getAdditionalCategoryStyles={this.getAdditionalCategoryStyles}
              toggleCategory={this.toggleCategory}
              isEditPinned={isEditPinned}
              disableStickyLabels={disableStickyOption}
              readOnly={readOnly}
              tabSelections={this.props.tabSelections}
              onTabSelectionChange={this.props.onTabSelectionChange}
            />

            <VisibilityChecker onVisibilityChange={this.onVisibilityChange} />
            <ComparisonScrollbarContainer
              className={comparisonScrollbarClasses}
              extraStyles={scrollbarContainerStyle}
              isEditPinned={isEditPinned}
              onScrollX={this.onScrollX}
              withPadding
              ref={ref => {
                this.comparisonScrollbarContainerRef = ref;
              }}
            >
              {items.map(this.renderCard(0, 5))}
              {this.state.shouldLoadAllCards ? (
                items.map(this.renderCard(6, 1000))
              ) : (
                <div className="loading-spinner-card">
                  <LoadingSpinner message={formatMessage('Comparison.LoadingMoreCards')} />
                </div>
              )}

              {this.displayExpandedComments() && items.some(i => !!i.markup) && commentLinesWidth && (
                // Lines are generated through a portal in the comparison value cell component
                <div className={classNames('comment-lines', { hidden: this.hideExpandedComments() })} style={{ width: commentLinesWidth }} />
              )}
            </ComparisonScrollbarContainer>

            {showActionBar && selectedComparisonItems && (
              <ActionBar
                comparison={comparison}
                isEditPinned={isEditPinned}
                items={selectedComparisonItems}
                unselectAll={this.props.unselectAllComparisonItems}
                onCombineClick={this.onCombineClick}
                onDeleteClick={this.props.onRemoveSelectedComparisonItems}
                onCopyClick={this.onCopyClick}
                onModifyClick={this.onModifyClick}
                onExpandClick={this.onExpandPortfolio}
                onToggleFXItem={this.onToggleFXItem}
                user={user}
              />
            )}

            {showTableStickyBars && (
              <>
                <div className="table-sticky-bar left" style={{ width: `${tableBarsWidth}px` }} />
                <div className="table-sticky-bar right" style={{ width: `${tableBarsWidth}px` }} />
              </>
            )}
          </div>
        </StickyContainer>

        <CorrelationModal item={showCorrelationModalItem} onCancel={this.onCloseCorrelationModal} comparison={comparison} />
      </>
    );
  }
}

const mapStateToProps = (state: ReduxState): StateProps => ({
  editedPortfolio: state.editPortfolio.portfolio,
  editedComparisonItem: state.editPortfolio.comparisonItem,
  selectedComparisonItems: state.comparison.selectedComparisonItems,
  isEditPinned: state.comparison.portfolioEditor.isPinned,
  editedBenchmark: state.editBenchmarks.editedBenchmark,
  locale: state.intl.locale,
});

export default withErrorBoundary(
  connect(mapStateToProps, {
    selectComparisonItem,
    unselectComparisonItem,
    unselectAllComparisonItems,
    selectCategory,
    toggleField,
    setExpandedFields,
    setMarkupComment,
    postMarkupComment,
    clearMarkup,
    getItemHoldingsData,
    setRiskPeriod,
    setCalendarYearPeriod,
    toggleItemFX,
    toggleCategory,
    updateComparisonItemHolding,
    updateComparisonItemColor,
    updateComparisonItemCategory,
    getPortfolioById,
    removeItemFromGroup,
  })(ComparisonTable),
);
